import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { send_component_save_request, send_request } from '../../utils/Request';
import { injectMetadataFields } from '../../utils/buildComponentSavePayload';
import useConditionalLogicStore, { getConditionalLogicStore } from './useConditionalLogicStore';
import AddIcon from '@mui/icons-material/Add';
import ItemLogic from './ConditionalLogicComponents/ItemLogic';
import './ConditionalLogicComponents/style.css';
import { getCurrentTime } from '../../utils/getCurrentTime';
import { Grid } from '@mui/material';
import Pagination from '@mui/lab/Pagination';
import useProjectStore from '../ProjectCanvas/useProjectStore';
import { IndividualComponentLoader } from '../../utils/ComponentLoadingSkeleton';

const ConditionalLogicTemplate = (props) => {
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [totalPage, setTotalPage] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsInPage, setItemsInPage] = useState(0);
  const [itemList, setItemList] = useState([]);
  const [description, setDescription] = useState(null);
  const [status, setStatus] = useState(null);

  const store = useMemo(() => getConditionalLogicStore(props.component_id), [props.component_id]);
  const data = store(s => s.data);

  const insertPlaceholder = useCallback((label, text, style) => {
    const { placeholders, setPlaceholders } = store.getState();
    setPlaceholders([...placeholders, { label, text, style }]);
  }, []);

  useEffect(() => {
    getConditionalLogicData(0);
  }, []);

  const getConditionalLogicData = useCallback((idx) => {
    if (props.component_id === undefined) return;

    // If store was prefilled from canvas load, skip the REST fetch
    if (store.getState().loaded) {
      props.onComponentNameChanged(store.getState().name);
      props.onLastModifiedChanged(store.getState().lastModified);
      setLoading(false);
      props.setChildTemplateLoaded(true);
      return;
    }

    setLoading(true);

    send_request(
      `project-service/project/component/query/${props.component_id}/at-version/${props.version}/${idx}`,
      '',
      {}
    )
      .then(response => {
        if (response && response.data) {
          if (response.data.componentData) {
            if (response.data.componentData.data) {
              let { componentData } = response.data;
              let checkKeyRandom = false;
              response.data.itemList.forEach(item => {
                if (!item.keyRandom) checkKeyRandom = true;
              });

              if (checkKeyRandom) {
                updateKeyRandom(
                  componentData.name,
                  response.data.description,
                  componentData.lastModified,
                  idx
                );
              } else {
                setTotalPage(response.data.totalPage);
                setItemsInPage(response.data.componentData.data.length);
                setCurrentPage(response.data.currentPage);
                setCurrentIndex(response.data.currentIndex);
                setItemList(response.data.itemList);

                store.getState().setConditionalLogicTemplate(componentData);
                store.setState({ loaded: true });
                props.onComponentNameChanged(store.getState().name);
                props.onComponentDescriptionChanged(response.data.description);
                props.onLastModifiedChanged(store.getState().lastModified);
              }

              setLoading(false);
            }
          } else {
            setItemsInPage(1);
          }
        }

        props.setChildTemplateLoaded(true);
        setLoading(false);
      })
      .catch(err => {
        throw Error(err.message);
      });
  }, [props.component_id, props.version]);

  const updateKeyRandom = useCallback((componentName, componentDescription, lastModified, idx) => {
    const reqData = {
      type: 'conditional_logic',
      componentData: {
        name: componentName,
        data: [],
        lastModified: lastModified,
        itemDelete: null,
      },
      componentId: props.component_id,
      description: componentDescription,
    };
    send_request(`component-service/conditional-logic/update-key-random`, reqData, '', 'POST').then(
      response => {
        if (response) getConditionalLogicData(idx);
      }
    );
  }, [props.component_id, getConditionalLogicData]);

  const saveComponent = useCallback((saveIndex, itemDelete) => {
    const templateName = store.getState().name;
    const lastModified = getCurrentTime();
    store.getState().setLastModified(lastModified);
    store.getState().setConditionalLogicName(templateName);

    const template = store.getState().getTemplate();

    const reqData = injectMetadataFields({
      type: 'conditional_logic',
      componentData: {
        name: template.name,
        data: template.data,
        lastModified: lastModified,
        itemDelete: itemDelete ? itemDelete : null,
      },
      componentId: props.component_id,
      description: store.getState().componentDescription,
      status: status,
    });

    send_component_save_request(
      `component-service/conditional-logic/data`,
      reqData,
      '',
      'POST'
    )
      .then(response => {
        if (response.status === 200) {
          props.showSuccess(reqData);
          props.onLastModifiedChanged(lastModified);
          useProjectStore.setState({ savedComponent: true });
        }
      })
      .catch(err => {
        throw err;
      });
  }, [props.component_id, status]);

  useEffect(() => {
    const unsub = useProjectStore.subscribe(
      s => s.selectedPlaceholder,
      (placeholder) => {
        const activeId = useProjectStore.getState().activeComponentId;
        if (activeId !== null && activeId !== props.component_id) return;
        if (!placeholder || !placeholder.selected) return;
        const { style, key } = placeholder.selected;
        insertPlaceholder(style.default, key, style);
      }
    );
    return unsub;
  }, [insertPlaceholder]);

  const handleKeyChanges = useCallback((err) => {
    setError(err);
  }, []);

  const handleChange = useCallback((event, value) => {
    const { placeholdersError } = store.getState();
    if (placeholdersError.length > 0) {
      props.showError();
      return;
    }
    saveComponent((value - 1) * 5, null);
  }, [saveComponent]);

  const handleAddItem = useCallback(() => {
    if (itemsInPage === 5) {
      const { placeholdersError } = store.getState();
      if (placeholdersError.length > 0) {
        props.showError();
        return;
      }
      store.getState().addLogic();
      saveComponent(currentIndex + 5, null);
    } else {
      store.getState().addLogic();
    }
    setItemsInPage(prev => prev + 1);
  }, [itemsInPage, currentIndex, saveComponent]);

  const copyThisLogic = useCallback((d) => {
    if (itemsInPage === 5) {
      const { placeholdersError } = store.getState();
      if (placeholdersError.length > 0) {
        props.showError();
        return;
      }
      store.getState().copyThisLogic(d);
      saveComponent(currentIndex + 5, null);
    } else {
      store.getState().copyThisLogic(d);
    }
    setItemsInPage(prev => prev + 1);
  }, [itemsInPage, currentIndex, saveComponent]);

  if (!loading) {
    return (
      <Grid container item xs={12} className="conditional-logic" direction={'column'}>
        {data.map((d, index) => (
          <ItemLogic
            isExpanded={props.isExpanded}
            key={d.keyRandom}
            data={d}
            indexLogic={index}
            availablePlaceholders={props.availablePlaceholders}
            handleKeyChanges={handleKeyChanges}
            keyRandom={d.keyRandom}
            conditionalWorkflowPath={false}
            itemList={itemList}
            copyThisLogic={() => copyThisLogic(d)}
            updateItemsInPage={() => setItemsInPage(prev => prev - 1)}
            componentStore={store}
            removeItem={() => {
              let newItems = itemsInPage - 1;
              let newIdx = currentIndex;
              if (newItems === 0) {
                newIdx = currentIndex - 5 < 0 ? currentIndex : currentIndex - 5;
              }
              saveComponent(newIdx, d.keyRandom);
              setItemsInPage(newItems);
            }}
          />
        ))}
        <Grid
          container
          item
          direction={'row'}
          style={{ display: 'flex', justifyContent: 'center', marginBottom: '30px' }}
        >
          {currentPage === totalPage ? (
            <div className="hover-add-button" onClick={handleAddItem}>
              <AddIcon /> Add Logic
            </div>
          ) : (
            ''
          )}
        </Grid>
        <div style={{ display: 'flex', justifyContent: 'end' }}>
          <Pagination
            count={totalPage}
            page={currentPage}
            color="primary"
            onChange={handleChange}
          />
        </div>
      </Grid>
    );
  }
  return <IndividualComponentLoader />;
};

export default ConditionalLogicTemplate;
